import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Star,
  Award,
  BookOpen,
  Coins,
  Zap,
  ExternalLink,
  X,
  Sparkles,
} from 'lucide-react';
import confetti from 'canvas-confetti';

const GameCompletionModal = ({ isOpen, onClose, userStats }) => {
  useEffect(() => {
    if (isOpen) {
      // Epic confetti sequence - 3 seconds continuous
      const duration = 3000;
      const end = Date.now() + duration;
      const colors = ['#FF6B6B', '#EA4B71', '#22C55E', '#F59E0B'];

      (function frame() {
        confetti({
          particleCount: 2,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors,
        });
        confetti({
          particleCount: 2,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
    }
  }, [isOpen]);

  const courseUrl = process.env.REACT_APP_COURSE_URL || 'https://www.automationacademy.online/';

  const stats = [
    { icon: Zap, label: 'XP Total', value: userStats.totalXp, color: 'text-warning' },
    { icon: Coins, label: 'Monedas', value: userStats.coins, color: 'text-warning' },
    { icon: Award, label: 'Logros', value: userStats.achievementsCount, color: 'text-success' },
    { icon: BookOpen, label: 'Conceptos', value: userStats.conceptsLearned, color: 'text-primary' },
  ];

  const courseFeatures = [
    'Programa completo de 6 semanas',
    'Aprende a crear tu agencia de automatización',
    'Workflows avanzados y casos de uso reales',
    'Estrategias para monetizar tus servicios',
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.5, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto glass rounded-2xl p-6 sm:p-8 bg-gradient-to-b from-[#1A1D3D] to-[#0F1229] border border-white/10"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-black/30 rounded-full p-1.5 hover:bg-black/50 transition-colors z-10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-text-secondary" />
            </button>

            {/* Pulsing trophy icon */}
            <div className="flex justify-center mb-6">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="text-8xl"
              >
                <Trophy className="w-20 h-20 text-warning" fill="currentColor" />
              </motion.div>
            </div>

            {/* Title and subtitle */}
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-3 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent"
              >
                ¡Felicidades! Has Dominado n8n
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-text-secondary text-lg"
              >
                Completaste los 19 niveles del juego
              </motion.p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="glass rounded-lg p-4 text-center border border-white/5"
                >
                  <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color}`} />
                  <div className="text-2xl font-heading font-bold text-text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-text-muted">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Motivational message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="glass rounded-lg p-6 mb-8 border border-white/5"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="text-text-secondary leading-relaxed">
                    <span className="text-text-primary font-semibold">¡Increíble!</span> Has
                    completado todos los niveles de n8n play y demostrado un dominio excepcional de
                    n8n workflow automation. Ahora estás listo para construir automatizaciones de
                    nivel enterprise en el mundo real.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Course CTA section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="relative overflow-hidden rounded-xl p-6 mb-6 bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/10 border-2 border-primary/30"
            >
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 blur-xl" />

              <div className="relative z-10">
                <h3 className="text-2xl font-heading font-bold text-text-primary mb-3 flex items-center gap-2">
                  🚀 ¿Listo para el Siguiente Nivel?
                </h3>
                <p className="text-text-secondary mb-6">
                  Lleva tus habilidades al máximo con el curso completo de n8n
                </p>

                {/* Course features */}
                <div className="grid sm:grid-cols-2 gap-2 mb-6">
                  {courseFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Star className="w-4 h-4 text-success flex-shrink-0 mt-0.5" fill="currentColor" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 glass rounded-lg border border-white/10 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-text-muted line-through text-lg">$200</span>
                      <span className="text-3xl font-heading font-bold text-success">$100</span>
                    </div>
                    <p className="text-sm text-warning font-medium">
                      ⏰ Promoción especial - Solo 25 plazas disponibles
                    </p>
                  </div>
                  <div className="text-center sm:text-right">
                    <div className="inline-block px-3 py-1 bg-success/20 border border-success/30 rounded-full text-success text-sm font-bold">
                      50% OFF
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href={courseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full btn-neon py-4 rounded-xl flex items-center justify-center gap-2 text-lg font-bold"
                  >
                    Ver Curso Completo
                    <ExternalLink className="w-5 h-5" />
                  </motion.button>
                </a>
              </div>
            </motion.div>

            {/* Action buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={onClose}
                className="flex-1 py-3 px-6 rounded-xl bg-background-paper/50 border border-white/10 text-text-secondary hover:border-white/20 hover:text-text-primary transition-all font-medium"
              >
                Volver al Dashboard
              </button>
            </motion.div>

            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GameCompletionModal;
